import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import type { UIMessage } from "@ai-sdk/react";
import { useAdsWorkspaceStore } from "@/features/ads";
import { env } from "@/config/env";
import { AGENT_PERSONAS, type AgentId } from "@/config/agents";
import { appEvents, type AppEvent } from "@/shared/lib/events";
import { useAgentChatStore } from "../../store/useAgentChatStore";
import { useChatAttachments } from "../../hooks/useChatAttachments";
import {
  useCompanionPromptRequests,
  type CompanionPromptRequestSender,
} from "../../hooks/useCompanionPromptRequests";
import { useCompanionToolDispatch } from "../../hooks/useCompanionToolDispatch";
import { useMarcusVoiceReplies } from "../../hooks/useMarcusVoiceReplies";
import { useManagedAgentChat } from "../../hooks/useManagedAgentChat";
import {
  isActiveCompanionWorkflow,
  resolveActiveCompanionAgentId,
} from "../../lib/activeCompanionWorkflows";
import { MARCUS_ADS_SLASH_COMMANDS, type MarcusSlashCommand } from "../../lib/marcusSlashCommands";
import { DefaultCompanionDockPanel, type CompanionPanelRenderer } from "../CompanionDockShell";
import type { AvatarHandle } from "./types";

const VIDEO_EDITOR_WORKFLOW_SLUG = "marcus-video-editor";
const VIDEO_EDITOR_AGENT_NAME = "marcus_video_editor";
const GROWTH_WORKFLOW_SLUG = "growth-copilot";
const GROWTH_AGENT_NAME = "marcus";
const MARCUS_ONBOARDING_COMMANDS = new Set([
  "/onboarding",
  "/tour",
  "/replay-tour",
  "run onboarding fresh",
]);
const GUIDED_FLOW_SPEECH_TIMEOUT_MS = 24_000;
type GuidedSpeechStatus = Extract<AppEvent, { type: "marcus-guided-flow:speech-complete" }>["status"];
type MarcusCursorArrivedEvent = Extract<AppEvent, { type: "marcus-cursor-arrived" }>;

function toCursorStepStatus(status: GuidedSpeechStatus) {
  return status === "error" ? "timeout" : status;
}

function isMarcusOnboardingCommand(text: string): boolean {
  return MARCUS_ONBOARDING_COMMANDS.has(text.trim().toLowerCase());
}

export interface FloatingAgentCompanionProps {
  force?: boolean;
  renderPanel?: CompanionPanelRenderer;
}

export function FloatingAgentCompanion({
  force = false,
  renderPanel,
}: FloatingAgentCompanionProps = {}) {
  const connectedSessionId = useAgentChatStore((s) => s.connectedSessionId);
  const connectedWorkflowSlug = useAgentChatStore((s) => s.connectedWorkflowSlug);
  const connectedContext = useAgentChatStore((s) => s.connectedContext);
  const overlayMounted = useAgentChatStore((s) => s.chatPanelOverlayMounted);
  const adsScopeWorkspaceId = useAdsWorkspaceStore((s) => s.scopeWorkspaceId);

  if (!connectedSessionId || !connectedWorkflowSlug) return null;
  if (overlayMounted && !force) return null;
  if (!isActiveCompanionWorkflow(connectedWorkflowSlug)) return null;

  const agentId = resolveActiveCompanionAgentId(connectedWorkflowSlug);
  if (!agentId) return null;

  if (connectedWorkflowSlug === VIDEO_EDITOR_WORKFLOW_SLUG) {
    return (
      <ManagedAgentCompanionInner
        key={connectedSessionId}
        sessionId={connectedSessionId}
        agentId={agentId}
        agentName={VIDEO_EDITOR_AGENT_NAME}
        context={connectedContext}
        dispatchToolActions
        renderPanel={renderPanel}
      />
    );
  }

  if (connectedWorkflowSlug === GROWTH_WORKFLOW_SLUG) {
    const contextWorkspaceId =
      typeof connectedContext?.workspaceId === "string" ? connectedContext.workspaceId : null;
    return (
      <ManagedAgentCompanionInner
        key={connectedSessionId}
        sessionId={connectedSessionId}
        agentId={agentId}
        agentName={GROWTH_AGENT_NAME}
        workspaceId={contextWorkspaceId ?? adsScopeWorkspaceId}
        context={connectedContext}
        dispatchToolActions
        renderPanel={renderPanel}
      />
    );
  }

  // Nova and Saga own their active companion panels directly. Keeping this
  // component limited to page-level Marcus surfaces prevents deprecated
  // department workflows from re-entering through the old generic branch.
  return null;
}

function ManagedAgentCompanionInner({
  sessionId,
  agentId,
  agentName,
  workspaceId,
  context,
  dispatchToolActions = false,
  renderPanel,
}: {
  sessionId: string;
  agentId: AgentId;
  agentName: string;
  workspaceId?: string | null;
  context?: Record<string, unknown> | null;
  dispatchToolActions?: boolean;
  renderPanel?: CompanionPanelRenderer;
}) {
  const persona = AGENT_PERSONAS[agentId];
  const { executeCompanionAction } = useCompanionToolDispatch();
  const preserveOpenForSlashCommandNavigation = useAgentChatStore(
    (s) => s.preserveOpenForSlashCommandNavigation,
  );

  const {
    messages: activeMessages,
    setMessages,
    status,
    sendMessage: sendManagedMessage,
    stopMessage: stopManagedMessage,
    queuedMessageCount,
  } = useManagedAgentChat({
    runtimeTarget: "unified",
    agentName,
    initialSessionId: sessionId,
    workspaceId,
    context,
    onToolResult: dispatchToolActions
      ? (event) => {
          if (!event.isError && event.output) {
            void executeCompanionAction(event.output);
          }
        }
      : undefined,
  });

  const avatarRef = useRef<AvatarHandle | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const avatarSpeakingStartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const avatarSpeakingStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastGuidedSpeechStepRef = useRef<string | null>(null);
  // Read latest messages without re-creating the send callbacks — used to
  // detect the genuine first user turn for session auto-titling.
  const activeMessagesRef = useRef(activeMessages);
  activeMessagesRef.current = activeMessages;
  const [inputValue, setInputValue] = useState("");
  const [avatarConnected, setAvatarConnected] = useState(false);
  const [avatarConnecting, setAvatarConnecting] = useState(false);
  const [avatarSpeaking, setAvatarSpeaking] = useState(false);
  const [avatarMuted, setAvatarMuted] = useState(false);
  const [voiceVolume, setVoiceVolume] = useState(0.9);
  const [voiceTurnEpoch, setVoiceTurnEpoch] = useState(0);
  const {
    attachedImages,
    isUploadingImage,
    attachmentError,
    handleImageAttach,
    removeAttachment: handleRemoveImage,
    clearAttachments,
    clearAttachmentError,
  } = useChatAttachments({
    allowVideo: false,
    drainPendingAttachment: "marcus",
  });

  const isLoading = status === "submitted" || status === "streaming";
  const hasValidAvatarId = Boolean(
    env.nodeEnv !== "test" && env.simliApiKey && persona.simliFaceId && persona.voiceId,
  );

  const clearAvatarSpeakingTimers = useCallback(() => {
    if (avatarSpeakingStartTimerRef.current) {
      clearTimeout(avatarSpeakingStartTimerRef.current);
      avatarSpeakingStartTimerRef.current = null;
    }
    if (avatarSpeakingStopTimerRef.current) {
      clearTimeout(avatarSpeakingStopTimerRef.current);
      avatarSpeakingStopTimerRef.current = null;
    }
  }, []);

  const scheduleAvatarSpeakingStart = useCallback(() => {
    if (avatarSpeakingStopTimerRef.current) {
      clearTimeout(avatarSpeakingStopTimerRef.current);
      avatarSpeakingStopTimerRef.current = null;
    }
    if (avatarSpeakingStartTimerRef.current) return;
    avatarSpeakingStartTimerRef.current = setTimeout(() => {
      avatarSpeakingStartTimerRef.current = null;
      setAvatarSpeaking(true);
    }, 80);
  }, []);

  const scheduleAvatarSpeakingStop = useCallback(() => {
    if (avatarSpeakingStartTimerRef.current) {
      clearTimeout(avatarSpeakingStartTimerRef.current);
      avatarSpeakingStartTimerRef.current = null;
    }
    if (avatarSpeakingStopTimerRef.current) {
      clearTimeout(avatarSpeakingStopTimerRef.current);
    }
    avatarSpeakingStopTimerRef.current = setTimeout(() => {
      avatarSpeakingStopTimerRef.current = null;
      setAvatarSpeaking(false);
    }, 220);
  }, []);

  const clearAvatarSpeech = useCallback(() => {
    clearAvatarSpeakingTimers();
    avatarRef.current?.clearQueue();
    setAvatarSpeaking(false);
  }, [clearAvatarSpeakingTimers]);

  const beginAvatarTurn = useCallback(() => {
    clearAvatarSpeech();
    avatarRef.current?.start?.();
    setVoiceTurnEpoch((epoch) => epoch + 1);
  }, [clearAvatarSpeech]);

  const handleStop = useCallback(() => {
    clearAvatarSpeech();
    setVoiceTurnEpoch((epoch) => epoch + 1);
    stopManagedMessage();
  }, [clearAvatarSpeech, stopManagedMessage]);

  useEffect(() => {
    return () => {
      clearAvatarSpeakingTimers();
    };
  }, [clearAvatarSpeakingTimers]);

  useMarcusVoiceReplies({
    enabled: hasValidAvatarId,
    activeMessages,
    status,
    avatarRef,
    avatarMuted,
    turnEpoch: voiceTurnEpoch,
  });

  useEffect(() => {
    if (agentName !== GROWTH_AGENT_NAME) return;
    let cancelled = false;
    const publishGuidedSpeechComplete = (
      event: MarcusCursorArrivedEvent,
      status: GuidedSpeechStatus,
      reason?: string,
    ) => {
      if (cancelled) return;
      appEvents.publish({
        type: "marcus-guided-flow:speech-complete",
        ...(event.stepId ? { stepId: event.stepId } : {}),
        target: event.target,
        ...(event.comment ? { comment: event.comment } : {}),
        status,
        ...(reason ? { reason } : {}),
      });
      if (event.stepId) {
        appEvents.publish({
          type: "marcus-cursor-step-complete",
          stepId: event.stepId,
          target: event.target,
          status: toCursorStepStatus(status),
        });
      }
    };
    const unsubscribe = appEvents.subscribe("marcus-cursor-arrived", (event) => {
      if (event.source !== "guided-flow") return;
      if (!event.comment) {
        publishGuidedSpeechComplete(event, "skipped", "missing guided-flow narration");
        return;
      }
      const speechKey = event.stepId ?? `${event.target}:${event.comment}`;
      if (lastGuidedSpeechStepRef.current === speechKey) {
        publishGuidedSpeechComplete(event, "skipped", "duplicate guided-flow narration");
        return;
      }
      lastGuidedSpeechStepRef.current = speechKey;
      if (avatarMuted || !hasValidAvatarId) {
        publishGuidedSpeechComplete(event, "skipped", "Marcus avatar narration unavailable");
        return;
      }
      const avatar = avatarRef.current;
      avatar?.start?.();
      avatar?.clearQueue();
      if (!avatar?.speakAndWait) {
        avatar?.speak(event.comment);
        publishGuidedSpeechComplete(event, "skipped", "Marcus avatar cannot report speech end");
        return;
      }
      void avatar
        .speakAndWait(event.comment, { timeoutMs: GUIDED_FLOW_SPEECH_TIMEOUT_MS })
        .then((result) => {
          publishGuidedSpeechComplete(event, result.status, result.reason);
        })
        .catch((error: unknown) => {
          publishGuidedSpeechComplete(
            event,
            "error",
            error instanceof Error ? error.message : "Marcus avatar speech failed",
          );
        });
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [agentName, avatarMuted, hasValidAvatarId]);

  const sendManagedPromptRequest = useCallback<CompanionPromptRequestSender>(
    (text, images, display) => {
      beginAvatarTurn();
      sendManagedMessage(text, images, display);
    },
    [beginAvatarTurn, sendManagedMessage],
  );
  useCompanionPromptRequests(sendManagedPromptRequest);

  // Marcus only: on the genuine first user turn, ask the session-owning hook
  // to replace the generic "Ads Campaign" placeholder with a topic-relevant
  // title (preset for slash commands, LLM-summarized for free text).
  const suggestSessionTitle = useCallback(
    (payload: { presetTitle?: string; firstMessage?: string }) => {
      if (agentName !== GROWTH_AGENT_NAME) return;
      if (!payload.presetTitle && !payload.firstMessage) return;
      if (activeMessagesRef.current.some((m) => m.role === "user")) return;
      appEvents.publish({
        type: "companion:session-title-suggested",
        sessionId,
        ...payload,
      });
    },
    [agentName, sessionId],
  );

  const handleSend = useCallback(() => {
    const hasContent = inputValue.trim() || attachedImages.length > 0;
    if (!hasContent) return;

    const text = inputValue.trim();
    const images = attachedImages.length > 0 ? [...attachedImages] : undefined;
    setInputValue("");
    clearAttachments();
    clearAttachmentError();
    if (agentName === GROWTH_AGENT_NAME && !images && isMarcusOnboardingCommand(text)) {
      appEvents.publish({ type: "marcus-onboarding:restart" });
      return;
    }
    if (text) suggestSessionTitle({ firstMessage: text });
    beginAvatarTurn();
    sendManagedMessage(text, images);
  }, [
    agentName,
    attachedImages,
    beginAvatarTurn,
    clearAttachments,
    clearAttachmentError,
    inputValue,
    sendManagedMessage,
    suggestSessionTitle,
  ]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    handleSend();
  };

  // Slash commands are Marcus-only today (the ads orchestrator). Other
  // managed surfaces (video editor) keep the plain text input.
  const slashCommands = useMemo<readonly MarcusSlashCommand[] | undefined>(
    () => (agentName === GROWTH_AGENT_NAME ? MARCUS_ADS_SLASH_COMMANDS : undefined),
    [agentName],
  );

  const handleSlashCommandSelect = useCallback(
    (cmd: MarcusSlashCommand) => {
      setInputValue("");
      clearAttachments();
      clearAttachmentError();

      // /onboarding bypasses the Marcus round-trip. Inject the command
      // bubble locally so the chat still shows visual feedback, then ask
      // the onboarding controller to start directly (no popup dialog —
      // selecting from the palette is the user's confirmation).
      if (cmd.dispatchKind === "publish-onboarding-start") {
        setMessages((prev) => [
          ...prev,
          {
            id: `slash-${cmd.id}-${Date.now()}`,
            role: "user",
            parts: [
              {
                type: "data-companion-command",
                data: {
                  kind: "command",
                  label: cmd.label,
                  description: cmd.description,
                  source: cmd.bubbleSource,
                },
              },
            ] as unknown as UIMessage["parts"],
          },
        ]);
        appEvents.publish({
          type: "marcus-onboarding:start-direct",
          label: cmd.label,
          speakIntro: true,
        });
        return;
      }

      if (cmd.sessionTitle) suggestSessionTitle({ presetTitle: cmd.sessionTitle });
      preserveOpenForSlashCommandNavigation();
      beginAvatarTurn();
      sendManagedMessage(cmd.hiddenPrompt, undefined, {
        kind: "command",
        label: cmd.label,
        description: cmd.description,
        source: cmd.bubbleSource,
      });
    },
    [
      beginAvatarTurn,
      clearAttachmentError,
      clearAttachments,
      preserveOpenForSlashCommandNavigation,
      sendManagedMessage,
      setMessages,
      suggestSessionTitle,
    ],
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeMessages, status]);

  const inputProps = {
    inputRef,
    inputValue,
    onInputChange: setInputValue,
    onSend: handleSend,
    onStop: handleStop,
    onKeyDown: handleKeyDown,
    isLoading,
    showMicButton: true,
    attachedImages,
    onImageAttach: handleImageAttach,
    onRemoveImage: handleRemoveImage,
    isUploadingImage,
    attachmentError,
    queuedMessageCount,
    slashCommands,
    onSlashCommandSelect: handleSlashCommandSelect,
  };

  const PanelRenderer = renderPanel ?? DefaultCompanionDockPanel;

  return (
    <PanelRenderer
      persona={persona}
      avatarRef={avatarRef}
      avatarProvider="simli"
      avatarConnected={avatarConnected}
      avatarConnecting={avatarConnecting}
      avatarSpeaking={avatarSpeaking}
      avatarMuted={avatarMuted}
      hasValidAvatarId={hasValidAvatarId}
      avatarStatusLabel={
        !hasValidAvatarId
          ? "Offline"
          : avatarConnected
            ? avatarSpeaking
              ? "Speaking"
              : "Live"
            : avatarConnecting
              ? "Connecting"
              : "Ready"
      }
      avatarStatusDotClass={
        avatarConnected
          ? avatarSpeaking
            ? "bg-primary"
            : "bg-emerald-400"
          : avatarConnecting
            ? "bg-amber-400"
            : hasValidAvatarId
              ? "bg-surface-highest"
              : "bg-red-400/60"
      }
      setAvatarConnected={setAvatarConnected}
      setAvatarConnecting={setAvatarConnecting}
      setAvatarMuted={setAvatarMuted}
      voiceVolume={voiceVolume}
      setVoiceVolume={setVoiceVolume}
      scheduleAvatarSpeakingStart={scheduleAvatarSpeakingStart}
      scheduleAvatarSpeakingStop={scheduleAvatarSpeakingStop}
      isListening={false}
      isSecureMicContext={false}
      micHint={null}
      toggleListen={() => {}}
      activeMessages={activeMessages}
      inputProps={inputProps}
      messagesEndRef={messagesEndRef}
      isLoading={isLoading}
    />
  );
}
